// frontend/src/hooks/useSites.ts — the sites read surface (list · detail).
// Project-scoped server state (FRONTEND-PATTERNS §State). Sites is the OFFSET
// worked example (CONVENTIONS §Pagination): the list is SERVER-narrowed +
// paginated — the query is keyed on the URL view-state so a page/filter/sort
// change refetches; `keepPreviousData` holds the prior page on screen (no
// flicker). A mutation invalidates `['sites', projectId]` to refresh list+detail.
//
// 401 is never touched here — the centralized apiClient owns it (ADR-003).

import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { SiteDetail, SitesListResponse } from '@ems-app/schemas/sites/responses';
import type { CreateSiteRequest, DesignationChangeRequest, DesignationCorrectionRequest, DesignationsRequest, LifecycleDatesCorrectionRequest, SolutionChangeRequest, SolutionConfigRequest, SolutionCorrectionRequest, UpdateSiteRequest } from '@ems-app/schemas/sites/requests';
import type { CountResponse } from '@ems-app/schemas/common/pagination';
import type { SiteLookupRow, SiteLookupRule } from '@ems-app/schemas/sites/lookup';
import { useMemo } from 'react';
import { apiClient } from '../lib/apiClient.js';
import { framed, type ListFrame } from '../lib/bands.js';
import type { SiteGroupBy, SitePlacement } from '@ems-app/schemas/sites/requests';
import { useLookup, type LookupSource } from './useLookup.js';
import { siteTypesKeys } from './useSiteTypes.js';
import type { FacedRow, ServerSource } from './useOptions.js';
import { refreshOnSettled } from '../lib/settled.js';

/** The view-state the list query travels on (page/filters/sort/group). Arrays are
 *  serialized to CSV on the wire (the backend accepts CSV or repeated params). */
export interface SitesListParams {
  limit: number;
  offset: number;
  q?: string;
  status?: string[];
  /** The "Zone & cluster" filter's kinded picks (`zone:<id>` · `cluster:<id>` — common/territory.ts), as the link holds them. */
  territory?: string[];
  /** The area picks — each means everything inside it (the read expands the subtree). */
  administrative_area_id?: number[];
  site_type_id?: number[];
  site_priority_id?: number[];
  site_category_id?: number[];
  tag_id?: number[];
  /** Where the site stands — a filter (ADR-078 §4: a binary attribute is no group axis). */
  placement?: SitePlacement;
  grid?: 'offgrid' | 'ongrid';
  queue?: string[];
  /** The family dimension — observation-registry keys (e.g. site.no_config). */
  observation?: string[];
  /** Which FACE the lens works — registry (the site record) or solution. */
  face?: 'registry' | 'solution';
  solar?: 'yes' | 'no';
  generator?: 'yes' | 'no';
  provider_gen?: 'yes' | 'no';
  cooling?: 'yes' | 'no';
  chemistry?: string[];
  provider?: string[];
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  group_by?: SiteGroupBy;
  /** The grouped view's open set — the opened bands' keys, or `*`; absent = folded (the arrival). */
  open?: string[];
}

/** The sites as a picker's source (ADR-080 §3 — the server kind, the lookup's
 *  rule applied by the server; the face is `useSiteFace()`). `valueKind: 'site'`
 *  stores `site:<id>` — the Location filter's grammar from day one (ADR-037 §2). */
export function siteSource(projectId: string, rule: SiteLookupRule, face: (row: SiteLookupRow) => FacedRow, opts: { site?: string; valueKind?: string } = {}): ServerSource<'sites'> {
  return { kind: 'server', projectId, lookup: { entity: 'sites', params: { rule, site: opts.site } }, face, valueKind: opts.valueKind };
}

/** The ROWS of a few sites by id — the site lookup's rows of given ids (ADR-078 §6): a surface that holds site ids
 *  asks "name these codes" (a handful), never a directory; `rule` names the rule the server applies (the viewer's
 *  reach decides absence — no key), and the rows share the picker's cache on the same source, so a site the field
 *  met by typing is known here too. Asks nothing for an empty list; `unavailable` holds the codes the server
 *  answered absent. */
export function useNamedSites(
  projectId: string,
  ids: readonly string[],
  opts: { rule?: SiteLookupRule; enabled?: boolean } = {},
): { byId: ReadonlyMap<string, SiteLookupRow>; naming: boolean; unavailable: ReadonlySet<string> } {
  const { rule = 'in_coverage', enabled = true } = opts;
  const source = useMemo<LookupSource<'sites'>>(() => ({ entity: 'sites', params: { rule } }), [rule]);
  const key = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids stand in by their joined key (a caller's fresh array each render)
  const stable = useMemo(() => [...new Set(ids)], [key]);
  const look = useLookup(projectId, source, stable, { open: false, enabled: enabled && stable.length > 0 });
  return { byId: look.known, naming: look.naming, unavailable: look.unavailable };
}

export const sitesKeys = {
  /** List key — also the invalidation PREFIX (covers detail). */
  all: (projectId: string) => ['sites', projectId] as const,
  list: (projectId: string, params: SitesListParams) => ['sites', projectId, 'list', params] as const,
  detail: (projectId: string, siteId: string) => ['sites', projectId, siteId] as const,
};

// Flatten to axios query params — arrays → CSV (empty omitted), undefined dropped.
function toWireParams(p: SitesListParams): Record<string, string | number> {
  const out: Record<string, string | number> = { limit: p.limit, offset: p.offset };
  if (p.q) out.q = p.q;
  if (p.face) out.face = p.face;
  if (p.sort_by) out.sort_by = p.sort_by;
  if (p.sort_dir) out.sort_dir = p.sort_dir;
  if (p.group_by) out.group_by = p.group_by;
  if (p.placement) out.placement = p.placement;
  if (p.grid) out.grid = p.grid;
  if (p.solar) out.solar = p.solar;
  if (p.generator) out.generator = p.generator;
  if (p.provider_gen) out.provider_gen = p.provider_gen;
  if (p.cooling) out.cooling = p.cooling;
  const csv = (k: string, v?: (string | number)[]) => {
    if (v && v.length > 0) out[k] = v.join(',');
  };
  csv('status', p.status);
  csv('territory', p.territory);
  csv('administrative_area_id', p.administrative_area_id);
  csv('site_type_id', p.site_type_id);
  csv('site_priority_id', p.site_priority_id);
  csv('site_category_id', p.site_category_id);
  csv('tag_id', p.tag_id);
  csv('chemistry', p.chemistry);
  csv('provider', p.provider);
  csv('queue', p.queue);
  csv('observation', p.observation);
  // The grouped view's open set (`*` = every band); absent = folded.
  csv('open', p.open);
  return out;
}

/** The list's answer, stamped with the FRAME it was asked in (`lib/bands` — the page renders the answer on screen in
 *  the frame that produced it, never the URL's). */
export type SitesListAnswer = SitesListResponse & { frame: ListFrame<SiteGroupBy> };

async function fetchSites(projectId: string, params: SitesListParams, signal?: AbortSignal): Promise<SitesListAnswer> {
  const r = await apiClient.get<SitesListResponse>(`/projects/${projectId}/sites`, { params: toWireParams(params), signal });
  return framed(params)(r.data);
}

/** The list's QUESTION — its own wire params less the page and the bands (`limit` · `offset` · `group_by` · `open`):
 *  which sites, in which order. The list's COUNT and every FILE of the view ask exactly this — one builder, so the
 *  three cannot disagree (a file never re-reads the address bar). */
export function sitesQuestion(params: Omit<SitesListParams, 'limit' | 'offset'>): Record<string, string> {
  const { limit: _l, offset: _o, group_by: _g, open: _p, ...wire } = toWireParams({ ...params, limit: 1, offset: 0 });
  return Object.fromEntries(Object.entries(wire).map(([key, value]) => [key, String(value)]));
}

/** The list's COUNT alone on the same predicates (ADR-078 §6) — the phone filter
 *  drawer's "Show results (n)", a background question (`quietBusy`). The band
 *  parameters never ride it: the count is Σ every band whatever is opened. */
export async function countSites(projectId: string, params: Omit<SitesListParams, 'limit' | 'offset'>, signal?: AbortSignal): Promise<number> {
  const r = await apiClient.get<CountResponse>(`/projects/${projectId}/sites/count`, { params: sitesQuestion(params), signal, quietBusy: true });
  return r.data.total;
}

export function useSitesList(
  projectId: string,
  params: SitesListParams,
  opts: { enabled?: boolean } = {},
): UseQueryResult<SitesListAnswer, Error> {
  return useQuery({
    queryKey: sitesKeys.list(projectId, params),
    // A register is NOT live (ADR-074 §2): fresh when opened and after the person's own act. A fast second question
    // cancels the first (the signal).
    queryFn: (ctx) => fetchSites(projectId, params, ctx.signal),
    // `opts.enabled` gates the FIRST fetch while a fresh entry waits for the
    // default saved view (ADR-057 §4) — one list fetch, not two, on weak networks.
    enabled: projectId.length > 0 && (opts.enabled ?? true),
    placeholderData: keepPreviousData, // hold the prior page while the next is in flight
  });
}

/** The site's detail read. `quiet` — a form's Load the latest: a site gone meanwhile answers the form, never the
 *  not-found page (ADR-084 §6). */
export async function fetchSiteDetail(projectId: string, siteId: string, quiet = false): Promise<SiteDetail> {
  const r = await apiClient.get<SiteDetail>(`/projects/${projectId}/sites/${siteId}`, quiet ? { quietNotFound: true } : undefined);
  return r.data;
}

export function useSiteDetail(projectId: string, siteId: string): UseQueryResult<SiteDetail, Error> {
  return useQuery({
    queryKey: sitesKeys.detail(projectId, siteId),
    queryFn: () => fetchSiteDetail(projectId, siteId),
    enabled: projectId.length > 0 && siteId.length > 0,
  });
}

// ── the site record + lifecycle verbs — every mutation invalidates the sites
// prefix (list + the open peek/detail refetch together). Errors bubble; the
// caller branches on the backend `code` (SITE_INCOMPLETE / NO_SEATS / …). ──

/** Create the draft row — the wizard's step-1 commit (name + cluster). */
export function useCreateSite(projectId: string): UseMutationResult<{ id: string }, Error, CreateSiteRequest> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: async (body) => {
      const r = await apiClient.post<{ id: string }>(`/projects/${projectId}/sites`, body);
      return r.data;
    },
    onSuccess: invalidate,
  });
}

/** Partial edit — the wizard's later step commits + the Edit-site drawer. */
export function useUpdateSite(projectId: string): UseMutationResult<unknown, Error, { siteId: string; body: UpdateSiteRequest }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, body }) => apiClient.patch(`/projects/${projectId}/sites/${siteId}`, body),
    onSuccess: invalidate,
  });
}

// The lifecycle verbs — each carries its DECLARED day (YYYY-MM-DD, ≤ today;
// absent = today): the operational start, the pause's start and end, the
// retirement day. The server refuses a future or out-of-order day
// (INVALID_LIFECYCLE_DATE).
export function useActivateSite(projectId: string): UseMutationResult<unknown, Error, { siteId: string; activatedOn?: string }> {
  const invalidate = useSeatsInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, activatedOn }) =>
      apiClient.post(`/projects/${projectId}/sites/${siteId}/activate`, activatedOn ? { activated_on: activatedOn } : {}),
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useSuspendSite(projectId: string): UseMutationResult<unknown, Error, { siteId: string; reason: string; suspendedOn?: string }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, reason, suspendedOn }) =>
      apiClient.post(`/projects/${projectId}/sites/${siteId}/suspend`, { suspend_reason: reason, ...(suspendedOn ? { suspended_on: suspendedOn } : {}) }),
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useResumeSite(projectId: string): UseMutationResult<unknown, Error, { siteId: string; resumedOn?: string }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, resumedOn }) => apiClient.post(`/projects/${projectId}/sites/${siteId}/resume`, resumedOn ? { resumed_on: resumedOn } : {}),
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useDecommissionSite(projectId: string): UseMutationResult<unknown, Error, { siteId: string; decommissionedOn?: string }> {
  const invalidate = useSeatsInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, decommissionedOn }) =>
      apiClient.post(`/projects/${projectId}/sites/${siteId}/decommission`, decommissionedOn ? { decommissioned_on: decommissionedOn } : {}),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** The correction door — repair the recorded lifecycle days in place (site:lifecycle). */
export function useCorrectLifecycleDates(projectId: string): UseMutationResult<unknown, Error, { siteId: string; body: LifecycleDatesCorrectionRequest }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, body }) => apiClient.patch(`/projects/${projectId}/sites/${siteId}/lifecycle-dates`, body),
    onSuccess: invalidate,
  });
}

/** Hard-delete a draft (the only hard-deletable state; site:write). */
export function useDeleteDraftSite(projectId: string): UseMutationResult<unknown, Error, { siteId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId }) => apiClient.delete(`/projects/${projectId}/sites/${siteId}`),
    // The lists refetch — NOT the gone draft's own read: refetched, it would 404 and the apiClient's not-found
    // redirect would beat the page's way back (the teams' delete precedent); a REFUSED delete leaves it, and it re-reads too.
    onSettled: refreshOnSettled((_d, { siteId }, err) => qc.invalidateQueries({ queryKey: sitesKeys.all(projectId), predicate: err ? undefined : (q) => q.queryKey[2] !== siteId })),
  });
}

// ── the reconciliation verbs (ADR-056 §2/§7) — every mutation invalidates the
// sites prefix (list + the open peek's detail refetch together). ──

function useSitesInvalidate(projectId: string): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: sitesKeys.all(projectId) });
}

/** A verb that moves the tier seats (an activation, a retirement, a live site's classification): the sites prefix AND
 *  the site types' list, whose tiers carry the free seats (ADR-080 §7 — at once after one's own change). */
function useSeatsInvalidate(projectId: string): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: sitesKeys.all(projectId) }), qc.invalidateQueries({ queryKey: siteTypesKeys.all(projectId) })]);
  };
}

/** Create the FIRST period (a not-configured site). */
export function useCreateSolutionConfig(projectId: string): UseMutationResult<unknown, Error, { siteId: string; body: SolutionConfigRequest }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, body }) => apiClient.put(`/projects/${projectId}/sites/${siteId}/solution-config`, body),
    onSuccess: invalidate,
  });
}

/** "The solution changed" — a new period since a date (splits the containing one). */
export function useSolutionChange(projectId: string): UseMutationResult<unknown, Error, { siteId: string; body: SolutionChangeRequest }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, body }) => apiClient.post(`/projects/${projectId}/sites/${siteId}/solution-config/changes`, body),
    onSuccess: invalidate,
  });
}

/** "Correct data" — repair a period's values in place. */
export function useSolutionCorrection(projectId: string): UseMutationResult<unknown, Error, { siteId: string; periodId: number; body: SolutionCorrectionRequest }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, periodId, body }) =>
      apiClient.patch(`/projects/${projectId}/sites/${siteId}/solution-config/periods/${periodId}`, body),
    onSuccess: invalidate,
  });
}

/** "Correct start date" — one boundary, one fact (the previous close follows). */
export function useStartDateCorrection(projectId: string): UseMutationResult<unknown, Error, { siteId: string; periodId: number; effectiveFrom: string; rowVersion?: number }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, periodId, effectiveFrom, rowVersion }) =>
      apiClient.put(`/projects/${projectId}/sites/${siteId}/solution-config/periods/${periodId}/start-date`, { effective_from: effectiveFrom, row_version: rowVersion }),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** Delete a period — the one before absorbs its time. */
export function useDeleteSolutionPeriod(projectId: string): UseMutationResult<unknown, Error, { siteId: string; periodId: number }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, periodId }) => apiClient.delete(`/projects/${projectId}/sites/${siteId}/solution-config/periods/${periodId}`),
    onSettled: refreshOnSettled(invalidate),
  });
}

// ── the designation timeline (the classification SCD — ADR-060 §3; site:write) ──

/** Create the FIRST designation period (classification begins — drafts too). */
export function useWriteDesignations(projectId: string): UseMutationResult<unknown, Error, { siteId: string; body: DesignationsRequest }> {
  const invalidate = useSeatsInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, body }) => apiClient.put(`/projects/${projectId}/sites/${siteId}/designations`, body),
    onSuccess: invalidate,
  });
}

/** "The classification changed" — a new period since a date ≤ today (backdating
 *  is first-class; an old date splits the containing period). */
export function useDesignationChange(projectId: string): UseMutationResult<unknown, Error, { siteId: string; body: DesignationChangeRequest }> {
  const invalidate = useSeatsInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, body }) => apiClient.post(`/projects/${projectId}/sites/${siteId}/designations/changes`, body),
    onSuccess: invalidate,
  });
}

/** "Correct data" — repair a designation period's classification in place. */
export function useDesignationCorrection(projectId: string): UseMutationResult<unknown, Error, { siteId: string; periodId: number; body: DesignationCorrectionRequest }> {
  const invalidate = useSeatsInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, periodId, body }) =>
      apiClient.patch(`/projects/${projectId}/sites/${siteId}/designations/periods/${periodId}`, body),
    onSuccess: invalidate,
  });
}

/** "Correct start date" — one boundary, one fact (the previous close follows). */
export function useDesignationStartDateCorrection(projectId: string): UseMutationResult<unknown, Error, { siteId: string; periodId: number; effectiveFrom: string; rowVersion?: number }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, periodId, effectiveFrom, rowVersion }) =>
      apiClient.patch(`/projects/${projectId}/sites/${siteId}/designations/periods/${periodId}/start-date`, { effective_from: effectiveFrom, row_version: rowVersion }),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** Delete a designation period — the one before absorbs its time (an active
 *  site's only period is server-blocked: ONLY_PERIOD_UNDELETABLE). */
export function useDeleteDesignationPeriod(projectId: string): UseMutationResult<unknown, Error, { siteId: string; periodId: number }> {
  const invalidate = useSeatsInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, periodId }) => apiClient.delete(`/projects/${projectId}/sites/${siteId}/designations/periods/${periodId}`),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** Dismiss the site's open suggestions (→ Ignored, restorable). */
export function useIgnoreSuggestions(projectId: string): UseMutationResult<unknown, Error, { siteId: string; field: string }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, field }) =>
      apiClient.post(`/projects/${projectId}/sites/${siteId}/solution-config/suggestions/${field}/ignore`, {}),
    onSettled: refreshOnSettled(invalidate),
  });
}

/** Restore one ignored suggestion back into the box. */
export function useRestoreSuggestion(projectId: string): UseMutationResult<unknown, Error, { siteId: string; field: string }> {
  const invalidate = useSitesInvalidate(projectId);
  return useMutation({
    mutationFn: ({ siteId, field }) =>
      apiClient.post(`/projects/${projectId}/sites/${siteId}/solution-config/suggestions/${field}/restore`, {}),
    onSettled: refreshOnSettled(invalidate),
  });
}
